/**
 * React Context for streaming citation data.
 *
 * Streamdown internally memo-izes rendered blocks and compares component
 * references shallowly. Passing sources/handlers as props via closures would
 * create new component references on every update, but Streamdown's Block memo
 * would still skip re-rendering already-mounted blocks — so citations inside
 * those blocks would never see updated sources during streaming.
 *
 * Context solves this because context updates propagate through memo boundaries
 * to any consumer, ensuring citation components always read the latest sources.
 */
import { createContext, useContext } from 'react';

import type { CitationSource, StreamingCitationSources } from '../../types';
import type { ReactNode } from 'react';

export type CitationContentOverrideProps = {
  source: CitationSource;
  onRemove?: () => void;
  removeDisabled?: boolean;
};

export type MarkdownRendererContextValue = {
  sources: StreamingCitationSources;
  /** Previous version's sources, used in diff mode to resolve deleted citations */
  diffSources?: StreamingCitationSources;
  onRemoveSource?: (source: CitationSource) => void;
  isRemoveSourceDisabled?: boolean;
  /** Override citation popover content rendering. Return null to use default shared rendering. */
  renderCitationContentOverride?: (
    props: CitationContentOverrideProps,
  ) => ReactNode | null;
};

export const MarkdownRendererContext =
  createContext<MarkdownRendererContextValue>({
    sources: [],
  });

export const useMarkdownRendererContext = (): MarkdownRendererContextValue =>
  useContext(MarkdownRendererContext);
